import {StoppageContent} from '@domain/stoppages'
import {Loader} from '@hconnect/uikit/src/lib2'
import Add from '@mui/icons-material/Add'
import Check from '@mui/icons-material/Check'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import LinkIcon from '@mui/icons-material/Link'
import {Button, Box, Modal, Typography, TextField, Stack, Alert} from '@mui/material'
import * as React from 'react'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import ReactQuill from 'react-quill'
import {useNavigate} from 'react-router'

import {useUpdateNode} from '../../../hooks/useUpdateNode'
import {FEATURE_NAMESPACE} from '../../../localization'
import {nodeTitleValidation, validateAll} from '../../../utils/validator'
import {DeletedStoppageWarning} from '../DeletedStoppageWarning'

import {CreateStoppageForm} from './CreateStoppage'
import {useFinishAnalysis} from './useFinishAnalysis'
import {useGetAnalysisSummary} from './useGetAnalysisSummary'

export interface AnalysisFinishProperties {
  analysisId?: string
  open: boolean
  setOpen: (open: boolean) => void
}

export const FinishAnalysisModal: React.FC<AnalysisFinishProperties> = ({
  analysisId,
  open,
  setOpen
}) => {
  const handleClose = (e) => {
    setOpen(false)
    e.stopPropagation()
  }
  const id = parseInt(analysisId ?? '')
  const {mutateAsync: updateNode} = useUpdateNode(id.toString())
  const {isFetching, data} = useGetAnalysisSummary(id)
  const finishAnalysis = useFinishAnalysis(analysisId)
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const navigate = useNavigate()
  const [addStoppage, setAddStoppage] = useState<boolean>(false)

  const [title, setTitle] = useState<string>(data?.title ?? '')
  const [validationError, setValidationError] = useState(new Map())

  const validatorConfig = new Map([['title', nodeTitleValidation]])

  const handleFinish = async (e) => {
    const validationError = validateAll(validatorConfig, {title: title})
    setValidationError(validationError)

    if (validationError.size === 0) {
      if (title !== data?.title) {
        await updateNode({title: title, id: 0})
      }

      await finishAnalysis.mutateAsync()
      handleClose(e)
    }
  }

  const getErrorText = (key: string) => {
    const errorKey = validationError.get(key)
    return errorKey && t(errorKey)
  }

  useEffect(() => {
    if (data && data.title) {
      setTitle(data.title)
    }
  }, [data])

  if (!data || isFetching) return <Loader height="60vh" />

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Box
          p={3}
          sx={(theme) => ({
            width: '50vw',
            maxHeight: '85vh',
            minWidth: 600,
            maxWidth: 600,
            background: theme.palette.background.paper,
            borderRadius: 1,
            overflow: 'auto'
          })}
        >
          {isFetching && <Loader height="60vh" />}
          {addStoppage ? (
            <CreateStoppageForm cancelForm={() => setAddStoppage(false)} analysisId={id} />
          ) : (
            <>
              <Box>
                <Box mb={4}>
                  <Typography
                    id="modal-modal-title"
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: 500,
                      fontSize: 20,
                      lineHeight: '24px'
                    }}
                  >
                    Analysis summary
                  </Typography>
                </Box>
                <Stack gap={3} direction={'column'}>
                  {data.stoppage?.isDeleted && <DeletedStoppageWarning sx={{mb: 1}} />}
                  <TextField
                    error={validationError.has('title')}
                    helperText={getErrorText('title')}
                    data-test-id="node-form-title"
                    fullWidth={true}
                    label={t('label.title')}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setTitle(event.target.value)
                    }
                    value={title}
                    required
                    size={'small'}
                  />
                  {!data.stoppage && (
                    <Alert severity={'warning'}>
                      <Stack
                        direction={'row'}
                        gap={2}
                        alignItems={'center'}
                        justifyContent={'center'}
                      >
                        <Typography>This analysis is not linked to a stoppage.</Typography>
                        <Stack direction={'row'}>
                          <Button
                            variant={'text'}
                            sx={{color: 'text.primary', fontWeight: 600}}
                            startIcon={<LinkIcon />}
                            onClick={() => navigate('../stoppages')}
                          >
                            Link stoppage
                          </Button>
                          <Button
                            variant={'text'}
                            startIcon={<Add />}
                            onClick={() => setAddStoppage(true)}
                          >
                            Add stoppage
                          </Button>
                        </Stack>
                      </Stack>
                    </Alert>
                  )}
                  <StoppageContent stoppage={data.stoppage} />
                  <Typography sx={{fontSize: 13}}>Description</Typography>
                  <Typography component={Box} variant="body1" mb={2} sx={{fontWeight: 500}}>
                    <ReactQuill value={data.description} readOnly={true} theme={'bubble'} />
                  </Typography>
                </Stack>
                <Box mb={4} sx={{maxHeight: 280, overflowY: 'auto'}}>
                  <Box
                    py={1}
                    px={2}
                    sx={(theme) => ({
                      background: theme.palette.action.hover,
                      borderRadius: 0.5,
                      color: theme.palette.text.secondary,
                      fontSize: '13px',
                      lineHeight: '16px'
                    })}
                  >
                    Likely causes
                  </Box>
                  {data.likelyCauses &&
                    data.likelyCauses.map((cause) => (
                      <Box
                        key={`cause=${cause.id}`}
                        px={2}
                        py={2.5}
                        sx={(theme) => ({
                          height: 60,
                          fontWeight: 500,
                          lineHeight: '22px',
                          boxShadow: `inset 0px -1px 0px ${theme.palette.divider}`
                        })}
                      >
                        <Typography sx={{display: 'inline'}} variant="body2">
                          {cause.title}
                        </Typography>
                        <Box sx={{float: 'right'}}>
                          {cause.taskCount > 0 && (
                            <Box
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            >
                              <ContentPasteOutlinedIcon
                                sx={(theme) => ({
                                  color: theme.palette.text.secondary,
                                  width: '15px',
                                  height: '15px',
                                  marginRight: 0.5
                                })}
                              />
                              <Typography
                                sx={(theme) => ({
                                  color: theme.palette.text.secondary,
                                  fontSize: 12,
                                  lineHeight: '16px'
                                })}
                              >
                                {cause.taskCount}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                </Box>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                  }}
                  mb={2}
                >
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      data-test-id="analysis-cancel-button"
                    >
                      {t('buttons.cancel')}
                    </Button>
                    <Button
                      startIcon={<Check />}
                      color="primary"
                      variant="contained"
                      onClick={handleFinish}
                      data-test-id="analysis-finish-button"
                    >
                      {t('buttons.finishAnalysis')}
                    </Button>
                  </Box>
                </Box>
                <Box textAlign={'center'}>
                  <Typography pr={2} sx={{fontSize: 12}}>
                    Once finished, it can be downloaded in full detail as a PDF
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  )
}
